/**
 * Чистка списка полей: отметил чекбоксами — отправил в архив (AK-750).
 *
 * Сценарий founder 25.08: «в поиске набирает значение, чекбоксами
 * прокликивает 1-2-5 полей и нажимает "в архив"». Это ОДНО действие
 * пользователя, и вести себя оно обязано как одно — включая неуспех
 * (`frontend-vite/CLAUDE.md`, раздел «Массовые действия»):
 *
 *   · один запрос на весь пакет (ручка принимает список), а не веер `forEach`;
 *   · отказ на одном поле не отменяет остальные и не прячет сделанное;
 *   · ОДИН итог словами, с именами полей и причинами — идентификаторов в
 *     тексте быть не должно;
 *   · обновление экрана — независимо от исхода: при частичном отказе список
 *     на сервере уже изменился.
 *
 * Здесь — всё, что можно проверить без DOM: разбор причины, счёт выборки,
 * сам запрос и текст итога.
 */
import { api, ApiError } from '@/lib/api';
import { pluralRu } from '@/lib/dateFmt';
import type { ToastKind } from '@/lib/toast';
import type { FieldItem } from '@/components/fields/FieldsTable';

/** Ответ POST /api/v1/fields/archive. */
interface ArchiveBatchResponse {
  archived_ids: string[];
  failed: { field_id: string; reason: string }[];
}

/** Минимум для причины — столько же требует бэкенд. */
export const ARCHIVE_REASON_MIN = 3;

/**
 * Разбор причины архивации.
 *
 * Три исхода вместо двух (канон «Ввод: пусто ≠ мусор»): пустое и слишком
 * короткое — это НЕ «причина не указана», а «так нельзя». Причина
 * обязательна, потому что через месяц на вопрос «почему этого поля нет»
 * отвечает только она.
 */
export function parseArchiveReason(
  draft: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const trimmed = draft.trim();
  if (trimmed.length < ARCHIVE_REASON_MIN) {
    return {
      ok: false,
      error: 'Напишите причину — хотя бы несколько слов, например «дубль импорта»',
    };
  }
  return { ok: true, value: trimmed };
}

/** Сводка по отмеченным полям — то, что человек увидит в диалоге. */
export interface SelectionStats {
  count: number;
  areaHa: number;
  /** Сколько из отмеченных заняты культурой в активном сезоне. */
  occupied: number;
}

/**
 * Счёт по отмеченным полям.
 *
 * `occupied` считаем отдельно и показываем в диалоге: архив поля уносит с
 * собой и его занятия (так работает ручка — семантику мы не меняли), а
 * после заливки плана-2027 это чаще всего именно плановые посевы. Молча
 * такое уносить нельзя.
 */
export function selectionStats(
  rows: readonly FieldItem[],
  selected: ReadonlySet<string>,
): SelectionStats {
  let areaHa = 0;
  let occupied = 0;
  let count = 0;
  for (const row of rows) {
    if (!selected.has(row.id)) continue;
    count += 1;
    areaHa += Number(row.area_ha ?? 0);
    if (row.current_crop) occupied += 1;
  }
  return { count, areaHa, occupied };
}

/** Итог пакета — один тост. */
export interface ArchiveOutcome {
  kind: ToastKind;
  message: string;
  /** Сколько полей реально ушло в архив (для сброса выбора). */
  archivedCount: number;
}

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** «1 поле · 2 поля · 5 полей» — склонение общим хелпером, не своим. */
const FIELD_FORMS = ['поле', 'поля', 'полей'] as const;

/** Имена, а не идентификаторы: «не удалось на поле f-8f3a…» никому не поможет. */
function nameOf(names: ReadonlyMap<string, string>, id: string): string {
  return names.get(id) ?? 'без названия';
}

/**
 * Текст и тон итога.
 *
 * Тон: всё получилось → success (самоскрывается); совсем ничего →
 * error; получилось наполовину → warning. Warning и error висят до закрытия
 * — список отказов не должен исчезнуть через четыре секунды, иначе человек
 * не узнает, какие поля остались.
 */
export function archiveOutcome(
  response: ArchiveBatchResponse,
  names: ReadonlyMap<string, string>,
  areaById: ReadonlyMap<string, number>,
): ArchiveOutcome {
  const archived = response.archived_ids;
  const failed = response.failed;
  const area = archived.reduce((sum, id) => sum + (areaById.get(id) ?? 0), 0);

  const okPart =
    archived.length > 0
      ? `В архив: ${archived.length} ${pluralRu(archived.length, FIELD_FORMS)} · ${HA.format(area)} га`
      : 'Ни одно поле не заархивировано';
  const failPart =
    failed.length > 0
      ? ` Не удалось: ${failed
          .map((f) => `${nameOf(names, f.field_id)} (${f.reason})`)
          .join('; ')}.`
      : '';

  const kind: ToastKind =
    archived.length === 0 ? 'error' : failed.length > 0 ? 'warning' : 'success';

  return {
    kind,
    message: `${okPart}.${failPart}`,
    archivedCount: archived.length,
  };
}

/**
 * Отправить пакет в архив. ОДИН запрос на всю выборку.
 *
 * Ручка принимает список именно для этого: `forEach(id => delete(id))` дал
 * бы N запросов и N уведомлений на одно действие человека.
 */
export async function archiveFieldsBatch(
  ids: readonly string[],
  reason: string,
  rows: readonly FieldItem[],
): Promise<ArchiveOutcome> {
  const names = new Map(rows.map((r) => [r.id, r.name]));
  const areaById = new Map(rows.map((r) => [r.id, Number(r.area_ha ?? 0)]));
  try {
    const resp = await api.post<ArchiveBatchResponse>('/api/v1/fields/archive', {
      field_ids: [...ids],
      reason,
    });
    return archiveOutcome(resp, names, areaById);
  } catch (err) {
    // Отказ всего пакета (нет прав, сезон закрыт, сеть) — тоже ОДИН итог.
    // Причину с сервера показываем дословно: «403» человеку ничего не скажет.
    return {
      kind: 'error',
      message:
        err instanceof ApiError
          ? `Не удалось заархивировать: ${err.detail}`
          : 'Не удалось заархивировать поля — попробуйте ещё раз',
      archivedCount: 0,
    };
  }
}

/**
 * Заархивировать ОДНО поле — действие из попапа поля на /map (AK-772).
 *
 * Ручка та же (`POST /fields/archive`), потому что контракт архивации один:
 * причина обязательна и попадает в карточку поля, занятия уходят вместе с
 * полем. Отдельная функция нужна ради ИТОГА словами: пакетный текст
 * («В архив: 1 поле · 0 га») на карте врал бы про гектары — площадь поля в
 * MVT-свойствах приходит уже отформатированной строкой, числа у карты нет.
 *
 * Возвращает готовый текст и тон — вызывающий только показывает тост.
 */
export async function archiveSingleField(
  fieldId: string,
  fieldName: string,
  reason: string,
): Promise<{ kind: ToastKind; message: string; archived: boolean }> {
  try {
    const resp = await api.post<ArchiveBatchResponse>('/api/v1/fields/archive', {
      field_ids: [fieldId],
      reason,
    });
    if (resp.archived_ids.includes(fieldId)) {
      return { kind: 'success', message: `Поле «${fieldName}» в архиве`, archived: true };
    }
    // Ручка ответила 200, но поле не прошло (сезон заперт, чужое хозяйство) —
    // это НЕ успех, и причину показываем дословно, а не «что-то пошло не так».
    const failed = resp.failed.find((f) => f.field_id === fieldId);
    return {
      kind: 'error',
      message: failed
        ? `Не удалось заархивировать «${fieldName}»: ${failed.reason}`
        : `Не удалось заархивировать «${fieldName}»`,
      archived: false,
    };
  } catch (err) {
    return {
      kind: 'error',
      message:
        err instanceof ApiError
          ? `Не удалось заархивировать «${fieldName}»: ${err.detail}`
          : `Не удалось заархивировать «${fieldName}» — попробуйте ещё раз`,
      archived: false,
    };
  }
}
